import type { DisplayZone, ResolvedDateTimeFormat } from "@/shared/datetime";
import { formatDuration } from "@/shared/time/formatDuration";

import type { TimelineTrackItem } from "./TimelineTrackItem";
import { DAY, spanOf, type TimeWindow } from "./window";

/** A time range as text, for accessible names, hover cards and popovers. */
export function formatRange(
  item: Pick<TimelineTrackItem<unknown>, "startTime" | "endTime">,
  format: ResolvedDateTimeFormat,
) {
  return {
    start: format.format(new Date(item.startTime), { time: "seconds" }),
    end: format.format(new Date(item.endTime), { time: "seconds" }),
    duration: formatDuration((item.endTime - item.startTime) / 1000),
  };
}

/**
 * The window as one line — "Apr 16, 2026 – Jul 22, 2026" — with the zone
 * it is read in beside it ("CDT", "UTC"), once, rather than on each end. A
 * window of a day or more reads as dates; a shorter one needs the clock on
 * both ends to be told apart from its neighbours. Read in `format`'s zone.
 */
export function formatWindow(
  window: TimeWindow,
  format: ResolvedDateTimeFormat,
): { text: string; zone: string } {
  const time = spanOf(window) >= DAY ? "none" : "minutes";
  const start = format.format(new Date(window.start), { time });
  const end = format.format(new Date(window.end), { time });
  return {
    text: `${start} – ${end}`,
    zone: shortZoneName(window.end, format.zone),
  };
}

/** The short name of `zone` at `ms` ("CDT", "GMT+2", "UTC"; local when `undefined`); "" when `Intl` cannot say. */
function shortZoneName(ms: number, zone: DisplayZone | undefined): string {
  try {
    return (
      new Intl.DateTimeFormat(undefined, {
        timeZoneName: "short",
        timeZone: zone,
      })
        .formatToParts(new Date(ms))
        .find((part) => part.type === "timeZoneName")?.value ?? ""
    );
  } catch {
    return "";
  }
}
